"use client";

import React, { useState } from "react";

import { Database } from "@punchup/data/supabase.types";
import { ReactionButton } from "./ReactionButton";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { createUserReactionForComment } from "@/data/commentReactions";
import { useFeatureGate } from "statsig-react";

interface CommentReactionButtonProps {
  commentId: string;
  initialClapCount: number;
  initialUserClapCount: number;
  authenticated: boolean;
  className?: string;
  onClap?: (commentId: string, clapCount: number) => Promise<void>;
}

export function CommentReactionButton({
  commentId,
  initialClapCount,
  initialUserClapCount,
  authenticated,
  className,
  onClap,
}: CommentReactionButtonProps) {
  const supabase = createClientComponentClient<Database>();
  const [currentTotalCount, setCurrentTotalCount] = useState(initialClapCount);
  const [currentUserCount, setCurrentUserCount] =
    useState(initialUserClapCount);

  // Use video_comments_stableid gate to control animation style
  const { gate } = useFeatureGate("video_comments_stableid");
  const isVideoCommentsEnabled = gate.value === true;

  const handleReaction = async (count: number) => {
    try {
      // Optimistically update the counts
      setCurrentTotalCount((prev) => prev + count);
      setCurrentUserCount((prev) => prev + count);

      // Submit to database
      await createUserReactionForComment(supabase, commentId, "clap", count);

      // Call parent handler if provided
      if (onClap) {
        await onClap(commentId, count);
      }
    } catch (error) {
      console.error("Error submitting comment reaction:", error);
      // Revert optimistic updates on error
      setCurrentTotalCount((prev) => prev - count);
      setCurrentUserCount((prev) => prev - count);
    }
  };

  return (
    <ReactionButton
      totalCount={currentTotalCount}
      userCount={currentUserCount}
      size="sm"
      className={className}
      onReaction={handleReaction}
      animationStyle={isVideoCommentsEnabled ? "dynamic" : "simple"}
      enableParticles={isVideoCommentsEnabled}
    />
  );
}
